import type { NativeText } from "./text";
import type { Point } from "../scene/model";

export const textDeformations = [
  ["none", "Без изгиба", 0],
  ["arc", "Арка", 50],
  ["wave", "Волна", 50],
  ["circle", "По кругу", 100],
  ["flag", "Флаг", 50],
] as const;
export interface TextDeformation {
  type: (typeof textDeformations)[number][0];
  amount: number;
}
export function assertTextDeformation(value: unknown) {
  if (value === undefined) return;
  const d = value as TextDeformation;
  if (
    !d ||
    typeof d !== "object" ||
    Array.isArray(d) ||
    !textDeformations.some(([type]) => type === d.type) ||
    !Number.isFinite(d.amount) ||
    Math.abs(d.amount) > 100 ||
    Object.keys(d).some((key) => !["type", "amount"].includes(key))
  )
    throw new Error("Повреждены настройки изгиба текста.");
}
export function curvedText(t: NativeText) {
  return (
    !!t.deformation &&
    t.deformation.type !== "none" &&
    Math.abs(t.deformation.amount) >= 1e-4
  );
}
/** A glyph follows the tangent; its line offset stays perpendicular to the path. */
export function textPose(t: NativeText, x: number, y: number) {
  if (!curvedText(t)) return { x, y, angle: 0 };
  const w = t.layoutWidth,
    h = t.layoutHeight,
    d = t.deformation!;
  let px = x,
    py = h / 2,
    angle: number;
  if (d.type === "arc" || d.type === "circle") {
    const sweep = (d.amount / 100) * Math.PI * (d.type === "circle" ? 2 : 1);
    const radius = w / sweep;
    angle = (x / w - 0.5) * sweep;
    px = w / 2 + radius * Math.sin(angle);
    py += radius * (Math.cos(sweep / 2) - Math.cos(angle));
  } else {
    const frequency = d.type === "flag" ? Math.PI : 2 * Math.PI;
    const phase = (frequency * x) / w;
    const amplitude = (w * 0.15 * d.amount) / 100;
    py += amplitude * Math.sin(phase);
    angle = Math.atan(((amplitude * frequency) / w) * Math.cos(phase));
  }
  const offset = y - h / 2;
  return {
    x: px - offset * Math.sin(angle),
    y: py + offset * Math.cos(angle),
    angle,
  };
}
export function posePoint(
  pose: ReturnType<typeof textPose>,
  x: number,
  y: number,
): Point {
  const c = Math.cos(pose.angle),
    s = Math.sin(pose.angle);
  return { x: pose.x + c * x - s * y, y: pose.y + s * x + c * y };
}
const boxes = new WeakMap<
  NativeText,
  { x: number; y: number; width: number; height: number }
>();
/** Conservative envelope, independent of DOM/font measurement and object transforms. */
export function textCurveBox(t: NativeText) {
  const cached = boxes.get(t);
  if (cached) return cached;
  let box = { x: 0, y: 0, width: t.layoutWidth, height: t.layoutHeight };
  if (curvedText(t)) {
    const overhang =
      Object.values(t.styles).reduce(
        (size, s) => Math.max(size, s.size ?? 0),
        t.size,
      ) /
        2 +
      t.strokeWidth;
    let left = Infinity,
      right = -Infinity,
      top = Infinity,
      bottom = -Infinity;
    for (let i = 0; i <= 256; i++) {
      const pose = textPose(t, (t.layoutWidth * i) / 256, t.layoutHeight / 2);
      for (const x of [-overhang, overhang])
        for (const y of [-t.layoutHeight / 2, t.layoutHeight / 2]) {
          const p = posePoint(pose, x, y);
          left = Math.min(left, p.x);
          right = Math.max(right, p.x);
          top = Math.min(top, p.y);
          bottom = Math.max(bottom, p.y);
        }
    }
    box = {
      x: left,
      y: top,
      width: Math.max(1, right - left),
      height: Math.max(1, bottom - top),
    };
  }
  boxes.set(t, box);
  return box;
}
